import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Search, Zap, HandCoins, ShieldCheck, ArrowRight, Lock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi } from "@/lib/api";
import { useUser } from "@/context/UserContext";
import { ListingCard } from "@/components/ListingCard";

const HERO = "https://images.pexels.com/photos/12565887/pexels-photo-12565887.jpeg?auto=compress&cs=tinysrgb&w=1920";

const Feature = ({ icon: Icon, title, text, tone }) => (
  <div className="p-6 rounded-2xl bg-white border border-slate-200">
    <span className={`w-11 h-11 rounded-xl grid place-items-center ${tone}`}><Icon size={20} /></span>
    <h3 className="font-heading text-lg font-semibold mt-4">{title}</h3>
    <p className="text-sm text-slate-600 mt-1 leading-relaxed">{text}</p>
  </div>
);

export default function Home() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [items, setItems] = useState([]);

  const api = useApi();
  const { me } = useUser();
  const approved = me?.status === "approved";
  useEffect(() => { if (approved) api("get", "/listings?limit=6").then(setItems); }, [approved, api]);

  const search = (e) => {
    e.preventDefault();
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (maxPrice) p.set("price_max", maxPrice);
    nav(`/cerca?${p.toString()}`);
  };

  return (
    <div>
      <section className="relative bg-slate-950 text-white overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/80 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 lg:py-32">
          <p className="text-xs font-medium uppercase tracking-[0.25em] text-blue-300 fade-up">Privati e rivenditori, un solo mercato</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight max-w-3xl mt-4 fade-up" style={{ animationDelay: "80ms" }}>
            Compra subito o fai la tua offerta al miglior prezzo.
          </h1>
          <p className="text-base md:text-lg text-slate-300 max-w-xl mt-5 fade-up" style={{ animationDelay: "160ms" }}>
            Veicoli usati provenienti da aste, con chilometraggio certificato e veicoli garantiti.
          </p>
          <form onSubmit={search} className="mt-10 bg-white rounded-2xl p-2 flex flex-col sm:flex-row gap-2 max-w-3xl shadow-2xl fade-up" style={{ animationDelay: "240ms" }}>
            <div className="flex-1 flex items-center gap-2 px-3">
              <Search size={18} className="text-slate-400" />
              <Input data-testid="hero-search-input-brand" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Marca o modello, es. Audi Q5" className="border-0 shadow-none focus-visible:ring-0 text-slate-900" />
            </div>
            <Input data-testid="hero-search-input-price" type="number" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder="Prezzo max €" className="sm:w-40 border-0 sm:border-l rounded-none shadow-none focus-visible:ring-0 text-slate-900" />
            <Button data-testid="hero-search-submit-button" className="rounded-xl bg-blue-600 hover:bg-blue-700 h-11 px-6">Cerca</Button>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 grid md:grid-cols-3 gap-5">
        <Feature icon={Zap} tone="bg-blue-50 text-blue-600" title="Compra subito" text="Blocca il veicolo al prezzo indicato con un clic, senza trattative." />
        <Feature icon={HandCoins} tone="bg-amber-50 text-amber-600" title="Offerta al ribasso" text="Proponi il tuo prezzo: il venditore può accettare in tempo reale." />
        <Feature icon={ShieldCheck} tone="bg-emerald-50 text-emerald-700" title="Iscritti verificati" text="Ogni iscritto viene verificato con documenti prima di accedere." />
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-slate-500">Ultimi arrivi</p>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-1">Veicoli in evidenza</h2>
          </div>
          <Link to="/cerca" data-testid="home-see-all-link" className="text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center gap-1">Vedi tutti <ArrowRight size={16} /></Link>
        </div>
        {approved ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="home-featured-grid">
            {items.map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
          </div>
        ) : (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center" data-testid="home-locked-listings">
            <Lock className="mx-auto text-blue-600" />
            <p className="font-heading text-xl font-semibold mt-3">I veicoli sono visibili solo agli iscritti approvati</p>
            <p className="text-slate-500 mt-1">Registrati come privato o rivenditore e carica i documenti richiesti.</p>
            <Button asChild className="mt-6 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="home-locked-cta">
              <Link to={me ? "/completa-registrazione" : "/registrati"}>{me ? "Completa la registrazione" : "Registrati ora"}</Link>
            </Button>
          </div>
        )}
      </section>

      <section className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-2xl sm:text-3xl font-semibold">Trova il tuo prossimo veicolo</h2>
            <p className="text-slate-400 mt-2">Ogni annuncio è selezionato e pubblicato dal team BuyNow.</p>
          </div>
          <Button asChild data-testid="home-browse-cta" className="rounded-full bg-white text-slate-900 hover:bg-slate-100 h-12 px-8"><Link to="/cerca">Sfoglia i veicoli</Link></Button>
        </div>
      </section>
    </div>
  );
}
